import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

import * as Api from "@modules/api";
import TranslatedText from "@components/items/display/TranslatedText";
import { HelpCenterAccordion } from "@components/layouts/b2c/helpCenter/GeneralQuestions";
import {
  createFaqEngagement,
  getFaqCategories,
  getFaqsByCategoryId,
  useGetFaqCategories,
  useGetFaqsByCategoryId,
} from "@lib/stores";
import { useInfiniteScroll } from "@utils/hooks/useInfiniteScroll";
import { SVG } from "@components/items/common/SVG";
import { PageSpinner } from "@components/layouts/content/PageSpinner";
import { Skeleton } from "@ui/skeleton";
import WajeezLogo from "@components/items/common/WajeezLogo";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import { useLanguage } from "@modules/localizations/LanguageContext";

const HelpCenter = () => {
  const [selectedFaqCategory, setSelectedFaqCategory] = useState<
    Api.Server.CategoryOutput | null | undefined
  >(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [engagedFaqsIds, setEngagedFaqsIds] = useState<string[]>([]);

  const { language } = useLanguage();

  const { data: faqCategories, status: faqCategoriesStatus } =
    useGetFaqCategories();
  const { data: categoryFaqs, status: categoryFaqsStatus } =
    useGetFaqsByCategoryId();

  const selectedCategoryFaqs = selectedFaqCategory?.id
    ? categoryFaqs?.[selectedFaqCategory?.id]
    : undefined;

  const { lastElementRef } = useInfiniteScroll(
    selectedCategoryFaqs?.meta.hasNextPage,
    setPageNumber
  );

  const onEngagementClick = (id: string, isHelpful: boolean) => {
    setEngagedFaqsIds((engagedFaqsIds) => [
      ...engagedFaqsIds.filter((f) => f !== id),
      id,
    ]);

    createFaqEngagement(
      {
        faqId: Number(id),
        isHelpful,
      },
      () => {
        setEngagedFaqsIds((engagedFaqsIds) =>
          engagedFaqsIds.filter((f) => f !== id)
        );
      }
    );

    setPageNumber(1);
  };

  const skeletonData: { id: string }[] = Array.from({ length: 5 }).map(
    (s, i) => {
      return {
        id: i + "",
      };
    }
  );

  useEffect(() => {
    getFaqCategories();
  }, []);

  useEffect(() => {
    setSelectedFaqCategory(faqCategories[0]);
  }, [faqCategories]);

  useEffect(() => {
    setEngagedFaqsIds(
      selectedCategoryFaqs?.data
        .filter((c) => c.engaged)
        .map((c) => c.id?.toString()) as string[]
    );
  }, [selectedCategoryFaqs?.data.length]);

  useEffect(() => {
    if (selectedFaqCategory?.id) {
      getFaqsByCategoryId({
        Id: selectedFaqCategory?.id,
        PageNumber: pageNumber,
        PageSize: 10,
      });
    }
  }, [selectedFaqCategory?.id, pageNumber]);

  const renderFaqCategories = () => {
    return faqCategories.map((c) => {
      return (
        <div
          key={c.id}
          className={`border-b px-[54px] pb-[10px] ${
            selectedFaqCategory?.id === c.id
              ? "border-b-primary text-primary"
              : "border-b-black text-black dark:border-b-white dark:text-white"
          } cursor-pointer text-[15px] font-medium`}
          style={{ WebkitTapHighlightColor: "transparent" }}
          onClick={() => {
            const currentPage = c.id
              ? categoryFaqs?.[c.id]?.meta.currentPage
              : 1;

            currentPage && setPageNumber(currentPage);
            setSelectedFaqCategory(c);
          }}
        >
          {c.name}
        </div>
      );
    });
  };

  const isLoadingOrContent =
    (faqCategoriesStatus !== "loading" &&
      faqCategories.length &&
      categoryFaqsStatus !== "loading" &&
      selectedCategoryFaqs) ||
    (categoryFaqsStatus === "loading" && selectedCategoryFaqs);

  return (
    <div className="flex h-full flex-col items-center justify-start p-10">
      <div className="flex w-full items-center justify-start gap-x-8 text-black dark:text-white">
        <WajeezLogo width={100} lang={language} />
        <TranslatedText
          text={`help-center.titles.${"help center"}`}
          className="text-[24px] font-bold"
        />
      </div>
      <div className="cus-scrollbar-w-none mb-auto w-full overflow-y-auto">
        <div className="relative w-full">
          <div className="w-full pb-[12px] pt-[29px]">
            {faqCategoriesStatus !== "loading" ? (
              <div className="flex w-full items-center justify-center">
                {renderFaqCategories()}
              </div>
            ) : (
              <PageSpinner />
            )}
          </div>
          {isLoadingOrContent ? (
            <div className="px-4">
              {selectedCategoryFaqs?.data && (
                <HelpCenterAccordion
                  data={selectedCategoryFaqs?.data.map((c, i) => {
                    return {
                      id: c.id,
                      trigger: (
                        <div
                          ref={
                            selectedCategoryFaqs?.data.length - 1 === i
                              ? lastElementRef
                              : null
                          }
                          className={`text-[15px] font-medium`}
                        >
                          {c.question}
                        </div>
                      ),
                      content: (
                        <div className="pt-[6px] text-[15px] text-[#898A8D] dark:text-white">
                          <p className="border-b-[0.5px] border-[#C6C6C899] pb-[29px]">
                            {c.answer}
                          </p>
                          <div className="mt-[13px] pl-[23px] pr-2">
                            {c.id &&
                            engagedFaqsIds?.includes(c.id.toString()) ? (
                              <div className="text-center text-[15px] text-[#898A8D] dark:text-white">
                                <TranslatedText text="help-center.titles.thanks for your opinion" />
                              </div>
                            ) : (
                              <div className="flex w-full items-center justify-between">
                                <div className="text-[15px] text-[#898A8D] dark:text-white">
                                  <TranslatedText text="help-center.titles.did you find this article useful?" />
                                </div>
                                <div className="flex items-center justify-center">
                                  <div
                                    className={`flex h-[42px] w-[42px] cursor-pointer items-center justify-center rounded-full bg-primary dark:bg-[#898A8D]`}
                                    style={{
                                      WebkitTapHighlightColor: "transparent",
                                    }}
                                    onClick={() => {
                                      const { id } = c;

                                      id &&
                                        onEngagementClick(id.toString(), true);
                                    }}
                                  >
                                    <SVG src={`/assets/icons/thumbs-up.svg`} />
                                  </div>
                                  <div
                                    className={`mr-[10px] flex h-[42px] w-[42px] cursor-pointer items-center justify-center rounded-full bg-primary dark:bg-[#898A8D]`}
                                    style={{
                                      WebkitTapHighlightColor: "transparent",
                                    }}
                                    onClick={() => {
                                      const { id } = c;

                                      id &&
                                        onEngagementClick(id.toString(), false);
                                    }}
                                  >
                                    <SVG
                                      src={`/assets/icons/thumbs-up.svg`}
                                      className="rotate-180"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ),
                    };
                  })}
                />
              )}
              {categoryFaqsStatus === "loading" && pageNumber > 1 && (
                <div className="flex w-full items-center justify-center">
                  <Loader2
                    className={`h-7 w-7 animate-spin text-black ltr:mr-2 rtl:ml-2 dark:text-white`}
                  />
                </div>
              )}
            </div>
          ) : faqCategoriesStatus !== "loading" && faqCategories.length ? (
            <div className="mt-[68px] flex w-full flex-col items-center justify-center gap-y-2 px-4">
              {skeletonData.map((s) => {
                return (
                  <div key={s.id}>
                    <Skeleton
                      className={`h-[54px] w-[900px] rounded-xl bg-[#f4f4f5] dark:bg-[#27272a]`}
                    ></Skeleton>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
        <div className="mt-[40px] flex flex-col items-center justify-center">
          <TranslatedText
            text={`help-center.titles.${"you can always contact us directly"}`}
            className="pb-5 text-[24px] font-bold text-black dark:text-white"
          />
          <Link to="/contact-us" className="block">
            <ShadcnButton className="mx-auto text-white">
              <TranslatedText
                text={`help-center.buttons.${"contact us"}`}
                className="font-medium text-white"
              />
            </ShadcnButton>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
